<template>
  <v-form class="password-form my-0 login">
    <div v-if="layout === 'vertical'">
      <div v-if="passwordHelpText" class="password-info" v-html="passwordHelpText"></div>
      <div v-if="requireCurrentPassword" class="validate-group">
        <v-text-field
          prepend-inner-icon="mdi-lock"
          id="current-user-password"
          v-model="currentPasswordInput"
          :error-messages="currentPasswordErrors"
          :placeholder="$t('password_current')"
          :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="() => (revealPassword = !revealPassword)"
          :type="revealPassword ? 'text' : 'password'"
          solo />
      </div>
      <div class="validate-group">
        <v-text-field
          prepend-inner-icon="mdi-lock"
          id="user-password"
          v-model="passwordInput"
          :error-messages="passwordErrors"
          :placeholder="$t('enter_new_password')"
          :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="() => (revealPassword = !revealPassword)"
          :type="revealPassword ? 'text' : 'password'"
          solo />
        <password v-model="password" :strength-meter-only="true"/>
        <div class="password-strength pb-2">{{ $t('password_strength') }}</div>
      </div>
      <div class="validate-group">
        <v-text-field
          prepend-inner-icon="mdi-lock"
          id="user-repeat-password"
          v-model="repeatPassword"
          :error-messages="repeatPasswordErrors"
          :placeholder="$t('reenter_password')"
          :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append="() => (revealPassword = !revealPassword)"
          :type="revealPassword ? 'text' : 'password'"
          solo />
      </div>
    </div>
    <div v-if="layout === 'horizontal'">
      <div class="password-group p-3">
        <div v-if="requireCurrentPassword" class="form-group row validate-group">
          <label class="col-sm-3 col-form-label text-sm-right">
            {{ $t('password_current') }}
          </label>
          <div class="col-sm-6">
            <v-text-field
              id="current-user-password"
              v-model="currentPasswordInput"
              :error-messages="currentPasswordErrors"
              :placeholder="$t('password_current')"
              :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="() => (revealPassword = !revealPassword)"
              :type="revealPassword ? 'text' : 'password'"
              solo />
          </div>
          <div class="col-sm-3 field-info">{{ $t('password_to_change_password_you_must_enter_current_password') }}</div>
        </div>
        <div class="form-group row validate-group">
          <label class="col-sm-3 col-form-label text-sm-right">{{ passwordLabel }}</label>
          <div class="col-sm-6">
            <v-text-field
              id="user-password"
              v-model="passwordInput"
              :error-messages="passwordErrors"
              :placeholder="$t('enter_new_password')"
              :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="() => (revealPassword = !revealPassword)"
              :type="revealPassword ? 'text' : 'password'"
              solo />
            <password v-model="password" :strength-meter-only="true"/>
          </div>
          <div v-if="passwordHelpText" class="col-sm-3 field-info" v-html="passwordHelpText"></div>
        </div>

        <div class="form-group row validate-group">
          <label class="col-sm-3 col-form-label text-sm-right">{{ repeatPasswordLabel }}</label>
          <div class="col-sm-6">
            <v-text-field
              id="user-repeat-password"
              v-model="repeatPassword"
              :error-messages="repeatPasswordErrors"
              :placeholder="$t('reenter_password')"
              :append-icon="revealPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="() => (revealPassword = !revealPassword)"
              :type="revealPassword ? 'text' : 'password'"
              solo />
          </div>
        </div>
      </div>
    </div>
  </v-form>
</template>

<script>
import Password from 'vue-password-strength-meter'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import { required, sameAs } from 'vuelidate/lib/validators'
import { isEmpty } from 'lodash'

const MIN_PASSWORD_LENGTH = 8

export default {
  name: 'PasswordChangeForm',
  components: {
    Password
  },
  mixins: [
    ValidationMixin
  ],
  props: {
    // the password
    password: String,
    // the users current password (required only if updating existing password)
    currentPassword: String,
    // Flag to indicate if the from should collect and require the user's current password
    requireCurrentPassword: Boolean,
    // the form layout to use
    layout: String,
    // The label text to use for the password field
    passwordLabel: String,
    // The label text to use for the repeat password field
    repeatPasswordLabel: String,
    // The password help info text
    passwordHelpText: String
  },
  data() {
    return {
      MIN_PASSWORD_LENGTH: MIN_PASSWORD_LENGTH,
      // the password, repeated
      repeatPassword: null,
      // Contains any error messages to be displayed
      errorMessage: null,
      // Whether to show or obscure the passwords
      revealPassword: false
    }
  },
  validations: {
    password: {
      required,
      minLength: function (value, vm) {
        if (!isEmpty(value)) {
          return value != null && value.length >= MIN_PASSWORD_LENGTH
        }
        return true
      },
      mustContainNumber: function (value, vm) {
        if (!isEmpty(value)) {
          return /\d/.test(value)
        }
        return true
      },
      mustContainLowerCase: function (value, vm) {
        if (!isEmpty(value)) {
          return /[a-z]/.test(value)
        }
        return true
      },
      mustContainUpperCase: function (value, vm) {
        if (!isEmpty(value)) {
          return /[A-Z]/.test(value)
        }
        return true
      }
    },
    repeatPassword: {
      sameAsPassword: sameAs('password')
    },
    currentPassword: {
      requiredWhen: function (value, vm) {
        if (!vm.requireCurrentPassword || !vm.$v.password.$dirty) {
          return true
        }
        return !isEmpty(value)
      }
    }
  },
  methods: {
    validate () {
      this.$v.$touch()
      return !this.$v.$invalid
    },

    reset () {
      this.passwordInput = ''
      this.currentPasswordInput = ''
      this.repeatPassword = ''
      this.$v.$reset()
    }
  },
  computed: {
    passwordInput: {
      get: function () {
        return this.password
      },
      set: function (value) {
        this.$emit('update:password', value)
      }
    },
    currentPasswordInput: {
      get: function () {
        return this.currentPassword
      },
      set: function (value) {
        this.$v.currentPassword.$touch()
        this.$emit('update:currentPassword', value)
      }
    },
    currentPasswordErrors () {
      return this.validationErrors([{
        prop: this.$v.currentPassword,
        rule: 'requiredWhen',
        message: this.$t('password_must_enter_current_password')
      }])
    },
    passwordErrors () {
      return this.validationErrors([{
        prop: this.$v.password,
        rule: 'required',
        message: this.$t('password_required')
      }, {
        prop: this.$v.password,
        rule: 'minLength',
        message: this.$t('password_min_length')
      }, {
        prop: this.$v.password,
        rule: 'mustContainLowerCase',
        message: this.$t('password_must_have_lower_case')
      }, {
        prop: this.$v.password,
        rule: 'mustContainUpperCase',
        message: this.$t('password_must_have_upper_case')
      }, {
        prop: this.$v.password,
        rule: 'mustContainNumber',
        message: this.$t('password_must_have_number')
      }])
    },
    repeatPasswordErrors () {
      return this.validationErrors([{
        prop: this.$v.repeatPassword,
        rule: 'sameAsPassword',
        message: this.$t('password_must_match')
      }])
    }
  }
}
</script>

<style scoped lang="scss">

.login-error {
  font-size: .85rem;
}

.resending-onetime-pass {
  color: rgb(51, 122, 183);
  font-size: 1.25rem;
  text-align: center;
}

.password-strength {
  font-size: .65rem;
  margin-top: -.65rem;
  color: rgb(112, 112, 112);
}

  /*
    When there's a colored background set, the password meter component looks
    a bit strange with white separators, so let's hack in a more suitable color
    by default.
  */
  .password-form ::v-deep(.Password__strength-meter:after),
  .password-form ::v-deep(.Password__strength-meter:before) {
    border-color: #bbb;
  }

.password-info {
  font-size: .85rem;
  margin-bottom: 4px;
}
</style>
